import React from 'react';
import Select from 'react-select';
import {
	getFDValue,
} from 'Vulture/Helpers';

class CrowFieldCustomVultureResolutionDropDown extends React.Component {
	constructor(props) {
		super(props);
		const savedResolution = getFDValue(props.fd, props.entity.name);
		const isCustomResolution = this.isCustomResolution(savedResolution, props.entity.options);

		this.state = {
			resolutionValue:       isCustomResolution ? 'custom' : savedResolution,
			orientationValue:      getFDValue(props.fd, props.entity.altName + '.orientation'),
			orientationOptions:    [],
			showCustomInput:       isCustomResolution,
			customResolution:      isCustomResolution ? savedResolution : '',
			customResolutionError: '',
		};

		this.validationTimeout = null;
	}

	isCustomResolution(resolution, options = []) {
		if (!resolution) return false;
		// Check if resolution is not in the predefined options list
		const predefinedValues = options.map((opt) => {
			return opt.value;
		});
		return resolution !== 'custom' && !predefinedValues.includes(resolution) && /^\d+x\d+$/.test(resolution);
	}

	shouldComponentUpdate(nextProps, nextState) {
		if (this.state !== nextState) return true;
		if (getFDValue(this.props.fd, this.props.entity.name) !== getFDValue(nextProps.fd, nextProps.entity.name)) return true;
		if (getFDValue(this.props.fd, this.props.entity.altName + '.orientation') !== getFDValue(nextProps.fd, nextProps.entity.altName + '.orientation')) return true;
		return false;
	}

	componentDidMount() {
		const { orientationOptions, orientationValue, } = this.computeOrientationValues();
		this.setState({
			orientationOptions,
			orientationValue,
		});
	}

	componentWillUnmount() {
		// Clear any pending validation timeout
		if (this.validationTimeout) {
			clearTimeout(this.validationTimeout);
		}
	}

	componentDidUpdate(prevProps, prevState) {
		const currentResolution = getFDValue(this.props.fd, this.props.entity.name);
		if (currentResolution !== prevState.resolutionValue) {
			const { orientationOptions, orientationValue, } = this.computeOrientationValues(true);
			this.setState({
				resolutionValue: currentResolution,
				orientationOptions,
				orientationValue,
			});
		}
	}

	validateCustomResolution(value) {
		const pattern = /^\d+x\d+$/;
		if (!value.trim()) {
			return 'Resolution is required';
		}
		if (!pattern.test(value)) {
			return 'Invalid format. Use widthxheight (e.g., 1920x1080)';
		}
		return '';
	}

	handleCustomResolutionChange(value) {
		// Update input value immediately for responsive typing
		this.setState({
			customResolution: value,
		});

		// Clear any existing timeout
		if (this.validationTimeout) {
			clearTimeout(this.validationTimeout);
		}

		// Debounce validation and expensive operations
		this.validationTimeout = setTimeout(() => {
			const error = this.validateCustomResolution(value);
			this.setState({
				customResolutionError: error,
			});

			if (!error) {
				this.setState({
					resolutionValue: value,
				}, () => {
					this.applyOrientationAndSave(value, true);
				});
			}
		}, 300); // Wait 300ms after user stops typing
	}

	computeOrientationValues(isNewValue = false, overrideResolution = null) {
		const {
			fd, entity,
		} = this.props;
		let {
			orientationOptions = [],
		} = entity || {};

		let originalOrientationValue = getFDValue(fd, entity.altName + '.orientation');
		let resolution = overrideResolution || getFDValue(fd, entity.name);

		// Use custom resolution if it's set and valid
		if (!overrideResolution && this.state.showCustomInput && this.state.customResolution && !this.state.customResolutionError) {
			resolution = this.state.customResolution;
		}

		let currentOrientationValue = originalOrientationValue;

		if (resolution && resolution !== 'custom') {
			let XY = resolution.split('x');
			let X = parseInt(XY[0], 10);
			let Y = parseInt(XY[1], 10);

			if (Y > X) {
				orientationOptions = [
					{
						value: 'portraitleft',
						label: 'Portrait Left',
					},
					{
						value: 'portraitright',
						label: 'Portrait Right',
					},
				];
			} else {
				orientationOptions = [
					{
						value: 'landscape',
						label: 'Landscape',
					},
					{
						value: 'landscapeflipped',
						label: 'Landscape (flipped)',
					},
				];
			}

			if (isNewValue || currentOrientationValue === undefined || currentOrientationValue === null) {
				currentOrientationValue = orientationOptions[0]?.value || 'landscape';
			} else {
				const isValidOption = orientationOptions.some((opt) => {
					return opt.value === currentOrientationValue;
				});
				if (!isValidOption) {
					currentOrientationValue = orientationOptions[0]?.value || 'landscape';
				}
			}
		} else if (isNewValue || currentOrientationValue === undefined || currentOrientationValue === null) {
			currentOrientationValue = 'landscape';
		}

		return {
			orientationOptions,
			orientationValue:    currentOrientationValue,
			orientationChanged:  currentOrientationValue !== originalOrientationValue,
		};
	}

	applyOrientationAndSave(resolutionValue, isNewValue = false) {
		const {
			app, entity,
		} = this.props;

		const { orientationOptions, orientationValue, orientationChanged, } = this.computeOrientationValues(isNewValue, resolutionValue);

		this.setState({
			orientationValue,
			orientationOptions,
		});

		if (orientationChanged) {
			app.setValue('system', [entity.name, entity.altName + '.orientation',], [resolutionValue, orientationValue,]);
		} else {
			app.setValue('system', entity.name, resolutionValue);
		}
	}

	render() {
		const {
			fd, entity, app,
		} = this.props;
		const {
			orientationValue, orientationOptions,
		} = this.state;

		let {
			attr = {}, options = [],
		} = entity || {};
		let defaultOption = null;
		const savedResolution = getFDValue(fd, entity.name);

		// Check if saved resolution is custom
		if (this.state.showCustomInput) {
			// Find the "Custom" option
			options.forEach((option, i) => {
				if (option.value === 'custom') {
					defaultOption = i;
				}
			});
		} else {
			options.forEach((option, i) => {
				if (option.value === savedResolution) {
					defaultOption = i;
				}
			});
		}

		let orientationIndex = 0;
		orientationOptions.forEach((option, i) => {
			if (option.value === orientationValue) {
				orientationIndex = i;
			}
		});

		return (
			<div className={`drop-down`}>
				<div className="label">
					<label>{attr.label}</label>
				</div>
				<Select
					className={entity.style}
					options={options}
					defaultValue={options[defaultOption]}
					onChange={(value) => {
						if (value.value === 'custom') {
							this.setState({
								value,
								resolutionValue:  value.value,
								showCustomInput:  true,
								customResolution: '',
							});
						} else {
							this.setState({
								value,
								resolutionValue:  value.value,
								showCustomInput:  false,
								customResolution: '',
							}, () => {
								this.applyOrientationAndSave(value.value, true);
							});
						}
					}}
				/>
				{this.state.showCustomInput && (
					<div className="text-small" style={{ marginTop: '1rem' }}>
						<div className="label">
							<label>Custom Resolution</label>
						</div>
						<input
							type="text"
							placeholder="Enter resolution (e.g., 1920x1080)"
							value={this.state.customResolution}
							onChange={(e) => {
								this.handleCustomResolutionChange(e.target.value);
							}}
							style={{
								borderColor: this.state.customResolutionError ? 'red' : undefined,
							}}
						/>
						{this.state.customResolutionError && (
							<p style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.25rem' }}>
								{this.state.customResolutionError}
							</p>
						)}
					</div>
				)}
				<div className="text-small">
					<div className="label">
						<label>Orientation</label>
					</div>
					<Select
						className={entity.style}
						options={orientationOptions}
						value={orientationOptions[orientationIndex]}
						onChange={(orientation) => {
							this.setState({
								orientationValue: orientation.value,
							});
							app.setValue('system', entity.altName + '.orientation', orientation.value);
						}}
					/>
				</div>
			</div>
		);
	}
}


if (!window.CrowFormField) {
	window.CrowFormField = {};
}

window.CrowFormField['custom.vulture.elements.resolution.drop.down'] = CrowFieldCustomVultureResolutionDropDown;
export default CrowFieldCustomVultureResolutionDropDown;